"use client";

import React, { useEffect, useRef, useState } from "react";

import Image from "next/image";
import clsx from "clsx";

interface GailMessageProps {
  message: string;
  animationSpeed?: number;
  startAnimation?: boolean;
  onAnimationComplete?: () => void;
  className?: string;
}

const GailMessage: React.FC<GailMessageProps> = ({
  message,
  animationSpeed = 20,
  startAnimation = true,
  onAnimationComplete,
  className,
}) => {
  const [typedMessage, setTypedMessage] = useState("");
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Function to simulate typing
  const typeText = async (text: string) => {
    setTypedMessage(""); // Reset text before starting animation

    const words = text.split(" ");
    let currentText = "";

    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? " " : "") + words[i];
      setTypedMessage(currentText);

      await new Promise((resolve) => {
        const speed =
          animationSpeed + (Math.random() - 0.5) * animationSpeed * 0.4;
        const timeoutId = setTimeout(resolve, speed);
        timeoutsRef.current.push(timeoutId);
      });
    }

    setIsAnimationComplete(true);
    if (onAnimationComplete) {
      onAnimationComplete();
    }

    return true;
  };

  // Start typing animation when component mounts or message changes
  useEffect(() => {
    if (startAnimation) {
      // Clear any existing timeouts
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      setIsAnimationComplete(false);

      typeText(message);
    } else {
      // If not animating, just show the full message
      setTypedMessage(message);
      setIsAnimationComplete(true);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, startAnimation, animationSpeed, onAnimationComplete]);

  return (
    <div className={clsx("max-w-[80%]", className)}>
      <div className="mb-2">
        <Image
          src="/images/gail_icon_black.svg"
          alt="Gail Icon"
          width={16}
          height={16}
          className="inline-block mr-2"
        />
        <span className="font-bold align-middle text-sm">• Gail</span>
      </div>
      <div className="text-sm leading-relaxed">
        {typedMessage}
        {!isAnimationComplete && (
          <span className="inline-block animate-pulse ml-1">▋</span>
        )}
      </div>
    </div>
  );
};

export default GailMessage;
